import AppButton from "@/components/shared/AppButton";
import { Box } from "@/components/ui/box";
import { ButtonSpinner } from "@/components/ui/button";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { T, useT } from "@transifex/react";
import type { TransactionDetails } from "ipctg-inflow-types/InFlowDB";
import { Pressable } from "react-native";

interface PromoCodeSectionProps {
  promoCode: string;
  isPromoCodeLoading: boolean;
  onChangePromoCode: (value: string) => void;
  onApplyPromoCode: () => void;
  isInvalid: boolean;
  isPromoApplied: boolean;
  onRemovePromoCode: () => void;
  onFocusInput: () => void;
  loadingRemovePromoCode: boolean;
  transactionDetails: TransactionDetails | null | undefined;
}

export const PromoCodeSection: React.FC<PromoCodeSectionProps> = ({
  promoCode,
  onChangePromoCode,
  onApplyPromoCode,
  isInvalid,
  isPromoApplied,
  onRemovePromoCode,
  onFocusInput,
  isPromoCodeLoading,
  loadingRemovePromoCode,
  transactionDetails,
}) => {
  const t = useT();
  return (
    <Box>
      {!isPromoApplied ? (
        <VStack className="gap-[10px]">
          <Text className="font-[600] leading-[20px] text-[14px] text-typography-900">
            <T _str="Promo or validation Code" />
          </Text>
          <HStack className="gap-[10px]">
            <Input
              className={`flex-1 h-[52px] pt-[8px] pb-[8px] pl-[4px] gap-[8px] rounded-[6px] border-[1px] ${isInvalid ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              isDisabled={isPromoCodeLoading}
            >
              <InputField
                autoFocus
                autoCapitalize="characters"
                className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                onChangeText={(text) => onChangePromoCode(text.trim().toUpperCase())}
                onFocus={() => onFocusInput()}
                placeholder={t("CODE")}
                placeholderTextColor="var(--color-typography-700)"
                value={promoCode.toUpperCase()}
              />
            </Input>
            <AppButton className="shrink" loading={isPromoCodeLoading} onPress={onApplyPromoCode} title={t("Apply")} variant="outline" disabled={!promoCode} />
          </HStack>
          {isInvalid && (
            <Text className="font-[600] leading-[20px] text-[14px] text-error-100">{t("This promo code ({promoCode}) is invalid.", { promoCode })}</Text>
          )}
        </VStack>
      ) : (
        <VStack className="gap-[4px]">
          <HStack className="justify-between">
            <Text className="font-[600] leading-[22px] text-[16px] text-typography-900">
              <T _str="Applied Promo/Validation Code:" />
            </Text>
            {!loadingRemovePromoCode ? (
              <Pressable onPress={onRemovePromoCode}>
                <Text className="font-[400] leading-[22px] text-[16px] text-typography-900 underline">
                  <T _str="Remove" />
                </Text>
              </Pressable>
            ) : (
              <ButtonSpinner color="text-typography-900" />
            )}
          </HStack>
          <Text className="font-[400] leading-[20px] text-[14px] text-typography-900">
            {transactionDetails?.promoCodeRedeemCopy || promoCode} {t("applied!")}
          </Text>
        </VStack>
      )}
    </Box>
  );
};
